import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShieldCheck, CheckCircle2, ChevronLeft, ArrowRight, Phone, AlertCircle, Calendar, ExternalLink, Award, FileText } from 'lucide-react';
import { allPolicies } from '../lib/policiesData';
import { useTranslation } from 'react-i18next';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE, ADVISOR_PHONE_DISPLAY } from '../components/SEOHead';
import { trackWhatsAppClick, trackPhoneClick } from '../services/analytics';

const PlanDetail = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';
  const { id } = useParams();
  const plan = allPolicies.find((p) => p.id === id);

  if (!plan) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 px-6">
        <SEOHead
          title="Plan Not Found | VSR Insurance Plus"
          description="The requested insurance policy could not be found. Explore our full list of LIC and HDFC ERGO plans."
          canonicalPath={`/plan/${id}`}
          noIndex={true}
        />
        <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-4">
          {isTe ? "పాలసీ కనుగొనబడలేదు" : "Plan Not Found"}
        </h1>
        <p className="text-slate-500 mb-6">
          {isTe ? "మీరు చూస్తున్న పాలసీ వివరాలు మార్చబడి ఉండవచ్చు." : "The policy details you are looking for may have been updated or moved."}
        </p>
        <div className="flex gap-4">
          <Link to="/lic-plans" className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors">
            {isTe ? "ఎల్‌ఐసి ప్లాన్లు చూడండి" : "Browse LIC Plans"}
          </Link>
          <Link to="/health-insurance" className="px-6 py-2.5 bg-slate-200 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl font-semibold hover:bg-slate-300 transition-colors">
            {isTe ? "హెల్త్ ప్లాన్లు చూడండి" : "Browse Health Plans"}
          </Link>
        </div>
      </div>
    );
  }

  const isLIC = plan.type === 'lic';
  const planName = isTe ? (plan.nameTe || plan.name) : plan.name;
  const planTag = isTe ? (plan.tagTe || plan.tag) : plan.tag;
  const planShortDesc = isTe ? (plan.shortDescTe || plan.shortDesc) : plan.shortDesc;
  const planInsurer = isTe ? (plan.officialInsurerTe || plan.officialInsurer) : plan.officialInsurer;
  const planFeatures = isTe && plan.featuresTe ? plan.featuresTe : plan.features;
  const planKeyBenefits = isTe && plan.keyBenefitsTe ? plan.keyBenefitsTe : plan.keyBenefits;
  const planLimitations = isTe && plan.limitationsTe ? plan.limitationsTe : plan.limitations;
  const planNotes = isTe && plan.advisorNotesTe ? plan.advisorNotesTe : plan.advisorNotes;
  const planCoverage = isTe ? (plan.coverageTe || plan.coverage) : plan.coverage;
  const planPremium = isTe ? (plan.premiumTe || plan.premium) : plan.premium;

  // Product / Service Schema for SEO
  const planSchema = {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    'name': plan.name,
    'description': plan.shortDesc,
    'provider': {
      '@type': 'Organization',
      'name': plan.officialInsurer
    },
    'broker': {
      '@type': 'Person',
      'name': ADVISOR_NAME,
      'jobTitle': 'Senior Insurance Advisor',
      'telephone': ADVISOR_PHONE
    },
    'category': isLIC ? 'Life Insurance' : 'Health Insurance',
    'feesAndCommissionsSpecification': 'Free advisory and zero fee consultation. Official insurer premium rates apply.'
  };

  const whatsappMessage = encodeURIComponent(
    isTe
      ? `నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను ${planName} (${plan.planNumber || ''}) పాలసీపై ఆసక్తి కలిగి ఉన్నాను. దయచేసి పూర్తి కొటేషన్ మరియు వివరాలు పంపగలరు.`
      : `Hello ${ADVISOR_NAME}, I am interested in ${plan.name} (${plan.planNumber || ''}). Please share the customized quote and brochure.`
  );

  return (
    <div className="min-h-screen pt-28 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? `${planName} వివరాలు, ప్రయోజనాలు & అర్హత | వి. ఎస్. రెడ్డి` : `${plan.name} Review, Benefits & Eligibility | VSR Insurance Plus`}
        description={isTe ? `${planName}: ${planShortDesc} అర్హత, హామీ ప్రయోజనాలు, వెయిటింగ్ పీరియడ్ మరియు సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి గారి ఉచిత సలహా.` : `${plan.name}: ${plan.shortDesc} Check eligibility, guaranteed benefits, waiting periods, and get expert consultation from V. S. Reddy.`}
        canonicalPath={`/plan/${plan.id}`}
        schemaJson={planSchema}
        breadcrumbs={[
          { name: isLIC ? (isTe ? 'ఎల్‌ఐసి పాలసీలు' : 'LIC Plans') : (isTe ? 'ఆరోగ్య బీమా' : 'Health Plans'), path: isLIC ? '/lic-plans' : '/insurance-plans' },
          { name: planName, path: `/plan/${plan.id}` }
        ]}
      />

      <div className="container mx-auto px-6 max-w-5xl">
        {/* Back Link */}
        <Link
          to={isLIC ? '/lic-plans' : '/insurance-plans'}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-800 dark:hover:text-white mb-8 transition-colors font-medium text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> {isLIC ? (isTe ? 'తిరిగి ఎల్‌ఐసి ప్లాన్లకు వెళ్లండి' : 'Back to LIC Plans') : (isTe ? 'తిరిగి హెల్త్ ప్లాన్లకు వెళ్లండి' : 'Back to Health Plans')}
        </Link>

        {/* Hero Section */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 lg:p-12 shadow-sm border border-slate-200 dark:border-slate-800 mb-8 relative overflow-hidden">
          <div
            className={`absolute top-0 right-0 w-80 h-80 ${isLIC ? 'bg-blue-500/5' : 'bg-rose-500/5'} rounded-full blur-3xl -translate-y-1/2 translate-x-1/2`}
          />

          <div className="relative z-10 flex flex-col lg:flex-row gap-8 justify-between items-start lg:items-center">
            <div className="max-w-2xl">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span
                  className={`inline-block px-3 py-1 ${
                    isLIC
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                  } rounded-full text-xs font-bold uppercase tracking-wider`}
                >
                  {planTag}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full">
                  {plan.planNumber || planInsurer}
                </span>
              </div>

              <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 dark:text-white mb-4 leading-tight">
                {planName}
              </h1>
              <p className="text-base lg:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                {planShortDesc}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {isTe ? "చివరి సమీక్ష: అక్టోబర్ 2026" : `Last Reviewed: ${plan.lastReviewed || 'October 2026'}`}
                </span>
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  {isTe ? "అధీకృత సంస్థ: " : "Insurer: "} {planInsurer}
                </span>
              </div>
            </div>

            <div className="w-full lg:w-auto p-6 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-800 lg:min-w-[280px] flex flex-col gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold mb-1">
                  {isTe ? "కవరేజ్ పరిమితి" : "Coverage / Sum Insured"}
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{planCoverage}</p>
              </div>
              <div className="h-[1px] w-full bg-slate-200 dark:bg-slate-800" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold mb-1">
                  {isTe ? "ప్రారంభ ప్రీమియం" : "Indicative Premium"}
                </p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">{planPremium}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Two Columns: Details vs Call to Action */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Key Features */}
            <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-3">
                <ShieldCheck className={`w-6 h-6 ${isLIC ? 'text-blue-500' : 'text-rose-500'}`} />
                {isTe ? "ముఖ్య పాలసీ ప్రయోజనాలు" : "Key Policy Features"}
              </h2>
              <ul className="grid sm:grid-cols-2 gap-4">
                {planFeatures.map((feature, idx) => (
                  <li key={idx} className="flex gap-3 items-start p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-100 dark:border-slate-800/60">
                    <CheckCircle2 className={`w-5 h-5 ${isLIC ? 'text-primary' : 'text-rose-500'} flex-shrink-0 mt-0.5`} />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Eligibility Details */}
            {plan.eligibility && (
              <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  {isTe ? "అర్హత & ప్రమాణాలు" : "Eligibility & Criteria"}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl">
                    <span className="text-xs text-slate-400 block font-medium uppercase mb-1">
                      {isTe ? "కనిష్ట చేరిక వయస్సు" : "Minimum Age at Entry"}
                    </span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {isTe ? (plan.eligibility.minAgeTe || plan.eligibility.minAge) : plan.eligibility.minAge}
                    </span>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl">
                    <span className="text-xs text-slate-400 block font-medium uppercase mb-1">
                      {isTe ? "గరిష్ట చేరిక వయస్సు" : "Maximum Age at Entry"}
                    </span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {isTe ? (plan.eligibility.maxAgeTe || plan.eligibility.maxAge) : plan.eligibility.maxAge}
                    </span>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl">
                    <span className="text-xs text-slate-400 block font-medium uppercase mb-1">
                      {isTe ? "కనిష్ట సమ్ అష్యూర్డ్" : "Minimum Sum Assured"}
                    </span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {isTe ? (plan.eligibility.minSumAssuredTe || plan.eligibility.minSumAssured) : plan.eligibility.minSumAssured}
                    </span>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl">
                    <span className="text-xs text-slate-400 block font-medium uppercase mb-1">
                      {isTe ? "ప్రీమియం చెల్లింపు కాలం" : "Premium Paying Term"}
                    </span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {isTe ? (plan.eligibility.pptTe || plan.eligibility.ppt) : plan.eligibility.ppt}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* In-depth Benefits */}
            {planKeyBenefits && planKeyBenefits.length > 0 && (
              <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">
                  {isTe ? "సమగ్ర ప్రయోజనాలు & రక్షణ" : "Detailed Benefits & Protection"}
                </h2>
                <div className="space-y-4">
                  {planKeyBenefits.map((benefit, idx) => (
                    <div key={idx} className="flex gap-3 items-start">
                      <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-primary font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        {idx + 1}
                      </div>
                      <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Limitations & Exclusions */}
            {planLimitations && planLimitations.length > 0 && (
              <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  {isTe ? "పరిమితులు & వెయిటింగ్ పీరియడ్ నిబంధనలు" : "Limitations & Important Exclusions"}
                </h2>
                <p className="text-xs text-slate-500 mb-4">
                  {isTe ? "పాలసీ తీసుకునే ముందు ప్రతి నిబంధనను పారదర్శకంగా పరిశీలించండి." : "Transparent underwriting disclosures to ensure you understand all terms before enrolling."}
                </p>
                <ul className="space-y-2.5">
                  {planLimitations.map((limit, idx) => (
                    <li key={idx} className="flex gap-2.5 items-start text-sm text-slate-600 dark:text-slate-400">
                      <span className="text-amber-500 font-bold">•</span>
                      <span>{limit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Advisor Insights */}
            {planNotes && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-800/80 dark:to-blue-950/40 p-8 rounded-3xl border border-blue-100 dark:border-blue-900/40">
                <div className="flex items-center gap-3 mb-4">
                  <Award className="w-6 h-6 text-yellow-500" />
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    {isTe ? "సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి గారి విశ్లేషణ (25+ ఏళ్ల అనుభవం)" : "Advisor Insights from V. S. Reddy (25+ Years Experience)"}
                  </h3>
                </div>
                <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed italic">
                  "{planNotes}"
                </p>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-blue-200/50 dark:border-slate-700">
                  <span>{isTe ? "అధికారిక సంస్థ: " : "Official Source: "} {planInsurer}</span>
                  {plan.officialSourceUrl && (
                    <a
                      href={plan.officialSourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {isTe ? "అధికారిక పోర్టల్" : "Insurer Portal"} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sticky CTA Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-slate-900 dark:bg-slate-850 text-white p-8 rounded-3xl shadow-xl border border-slate-800">
              <span className="text-xs font-bold text-yellow-400 uppercase tracking-wider block mb-2">
                {isTe ? "నేరుగా అడ్వైజర్ సేవలు" : "Direct Advisor Support"}
              </span>
              <h3 className="text-2xl font-bold mb-2">
                {isTe ? `${planName} పై ఆసక్తి ఉందా?` : `Interested in ${plan.name}?`}
              </h3>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                {isTe ? "మీ వయస్సు మరియు కుటుంబ అవసరాలకు తగినట్లుగా వ్యక్తిగత ప్రీమియం కొటేషన్ మరియు పూర్తి వివరాలను పొందండి." : "Receive a precise customized calculation based on your age, coverage requirements, and tax saving preferences."}
              </p>

              <a
                href={`https://wa.me/919849664800?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('plan_detail_sidebar', { plan: plan.id })}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold shadow-lg shadow-emerald-900/30 transition-all flex items-center justify-center gap-2 mb-3"
              >
                <Phone className="w-4 h-4" /> {isTe ? "వాట్సాప్ కొటేషన్ పొందండి" : "WhatsApp Quote Now"}
              </a>

              <a
                href={`tel:${ADVISOR_PHONE}`}
                onClick={() => trackPhoneClick('plan_detail_sidebar')}
                className="w-full py-3.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-semibold border border-slate-700 transition-all flex items-center justify-center gap-2 mb-4"
              >
                <Phone className="w-4 h-4 text-sky-400" /> {isTe ? `కాల్: ${ADVISOR_PHONE_DISPLAY}` : `Call ${ADVISOR_PHONE_DISPLAY}`}
              </a>

              <Link
                to="/contact"
                className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 text-center"
              >
                {isTe ? "ఉచిత కన్సల్టేషన్ బుక్ చేయండి" : "Book Doorstep or Video Consultation"} <ArrowRight className="w-4 h-4" />
              </Link>

              <div className="mt-6 pt-6 border-t border-slate-800 text-xs text-slate-400 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                  <span>{isTe ? "100% ఉచిత సలహా — ఎలాంటి బ్రోకరేజ్ లేదు" : "100% Free Consultation — No Brokerage"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                  <span>{isTe ? "హైదరాబాద్‌లో ఇంటి వద్దకే సేవలు" : "Doorstep Service in Hyderabad"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                  <span>{isTe ? "విదేశీ ఎన్‌ఆర్‌ఐలకు రిమోట్ మెయిల్ ఆర్డర్ (MOB)" : "Remote Mail Order for Global NRIs"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanDetail;
